import React, { FC, useEffect, useState } from "react";
// @ts-ignore
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "redux/store";
import {
  createProperties,
  getallupdateProperties,
  getCancellation,
  getPropertyCategories,
  getPropertyType,
  setId,
  updateProperties,
} from "../../redux/reducers/Properties/PropertiesSlice";
import { useNavigate, useParams } from "react-router-dom";
import FormItem from "./FormItem";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Input from "shared/Input/Input";
import Select from "shared/Select/Select";
import FormField from "shared/FormField/FormField";
import __countryListing from "../../data/jsons/__countryListing.json";
import { descriptionValidationSchemas } from "utils/formSchema";
import Textarea from "shared/Textarea/Textarea";
import { toast } from "react-toastify";
import CommonLayoutV2 from "./CommonLayoutV2";
import { calculateStepCompletion, checkExistingCompletion } from "utils/helpers/listingCompletionHelper";
import { captureError } from "utils/sentry";


export interface DescriptionProps {}

const Description: FC<DescriptionProps> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { slug } = useParams();
  const id = slug;

  const [filteredPropertyTypes, setFilteredPropertyTypes] = useState<any[]>([]);
  const [filteredCancellationTypes, setfilteredCancellationTypes] = useState<any[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [cachedPropertyType, setCachedPropertyType] = useState<string | null>(null);
  const [showPolicyDescription, setShowPolicyDescription] = useState(false);

  // Track step completion status
  const [stepCompletionStatus, setStepCompletionStatus] = useState({
    1: { completed: false, fieldsCompleted: 0, totalFields: 7 },
    2: { completed: false },
    3: { completed: false },
    4: { completed: false },
    5: { completed: false },
    6: { completed: false },
    7: { completed: false },
    8: { completed: false },
    9: { completed: false },
  });

  const {
    globalId,
    propertiesCategories,
    propertyTypes,
    PropertyToEdit,
    cancelTypes,
  } = useSelector((state: RootState) => state.property);
  const RouteID = slug;

  const initialValues = {
    title: PropertyToEdit?.title || "",
    property_category: PropertyToEdit?.property_category?.id || "",
    property_type_id: PropertyToEdit?.property_type?.id || "",
    country: PropertyToEdit?.country || "",
    street: PropertyToEdit?.address || "",
    city: PropertyToEdit?.city || "",
    state: PropertyToEdit?.state || "",
    zipcode: PropertyToEdit?.zipcode || "",
    guests: PropertyToEdit?.guest_number || 0,
    description: PropertyToEdit?.description || "",
    cancellation_policy_id: PropertyToEdit?.cancellation_policy?.id ? String(PropertyToEdit.cancellation_policy.id) : "",
    bedroom_description: PropertyToEdit?.bedroom_description,
    university_nearby: PropertyToEdit?.university_nearby,
    about_neighbourhoods: PropertyToEdit?.about_neighbourhoods,
  };

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: descriptionValidationSchemas,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      const payload = {
        title: values.title,
        property_category_id: values.property_category,
        property_type_id: values.property_type_id,
        address: values.street,
        city: values.city,
        state: values.state,
        country: values.country,
        zipcode: values.zipcode,
        guest_number: values.guests,
        description: values.description,
        cancellation_policy_id: values.cancellation_policy_id,
        bedroom_description: values.bedroom_description,
        university_nearby: values.university_nearby,
        about_neighbourhoods: values.about_neighbourhoods,
        ...(slug ? { id: slug } : {}),
      };

      setSubmitting(true);

      if (id) {
        if (isEditing) {
          dispatch(updateProperties({ propertyId: id, property: payload }))
            .unwrap()
            .then((response) => {
  const newSlug = response?.property?.slug || slug;
  if (response?.success) {
    toast.success("Updated successfully!");
    navigate(`/add-listing-2/${newSlug}`);
    resetForm();
  } else {
    toast.error("Failed to update. Please try again.");
  }
})
.catch((error) => {
  toast.error("Something went wrong. Please try again.");
  captureError(error, { stage: "description_update", propertyId: id });
})
.finally(() => setSubmitting(false));
        } else {
          navigate(`/add-listing-2/${slug}`);
        }
      } else {
        dispatch(createProperties({ property: payload }))
          .unwrap()
          .then((response) => {
  if (response?.success) {
    toast.success("Created successfully!");
    dispatch(setId(response.property.id));
    resetForm();
    navigate(`/add-listing-2/${response?.property?.slug}`);
  } else {
    toast.error("Failed to create property. Please try again.");
  }
})
.catch((error) => {
  toast.error("Something went wrong. Please try again.");
  captureError(error, { stage: "description_create", propertyId: id });
})
.finally(() => setSubmitting(false));
      }
    },
  });

  const initialFormValues = JSON.stringify(initialValues);

  useEffect(() => {
    const currentFormValues = JSON.stringify(formik.values);
    setIsEditing(currentFormValues !== initialFormValues);
  }, [formik.values, initialFormValues]);

  // Calculate completion for Step 1 using helper
  useEffect(() => {
    const requiredFields = [
      "title",
      "property_category",
      "property_type_id",
      "country",
      "guests",
      "description",
      "cancellation_policy_id",
    ];

    const hasErrors = Object.keys(formik.errors).length > 0;
    const completion = calculateStepCompletion(formik.values, requiredFields, hasErrors);

    setStepCompletionStatus((prev) => ({
      ...prev,
      1: completion,
    }));
  }, [formik.values, formik.errors]);

  // Load completion status from PropertyToEdit using helper
  useEffect(() => {
    if (PropertyToEdit) {
      const existingCompletion = checkExistingCompletion(PropertyToEdit);
      
      setStepCompletionStatus((prev) => ({
        ...prev,
        ...existingCompletion,
      }));
    }
  }, [PropertyToEdit]);

  // Show policy description if there's already a selected policy on load or when it changes
  useEffect(() => {
    if (formik.values.cancellation_policy_id && filteredCancellationTypes.length > 0) {
      setShowPolicyDescription(true);
    } else {
      setShowPolicyDescription(false);
    }
  }, [formik.values.cancellation_policy_id, filteredCancellationTypes]);

  const handleCancellationPolicyChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    formik.handleChange(event);
    setShowPolicyDescription(!!value);
  };

  useEffect(() => {
    dispatch(getPropertyCategories());
    dispatch(getPropertyType());
    dispatch(getCancellation());
    const propertyId = id;
    dispatch(getallupdateProperties({ propertyId }));
  }, [dispatch, globalId, id]);

  useEffect(() => {
    if (Array.isArray(propertyTypes)) {
      setFilteredPropertyTypes(propertyTypes);
    }
  }, [propertyTypes]);

  useEffect(() => {
    if (Array.isArray(cancelTypes)) {
      setfilteredCancellationTypes(cancelTypes);
    }
  }, [cancelTypes]);

  useEffect(() => {
    if (PropertyToEdit?.property_type?.name) {
      setCachedPropertyType(PropertyToEdit.property_type.name);
    }
  }, [PropertyToEdit?.property_type?.name]);

  // Get selected policy details
  const getSelectedPolicy = () => {
    return filteredCancellationTypes.find(
      (type) => String(type.id) === String(formik.values.cancellation_policy_id)
    );
  };

  return (
    <CommonLayoutV2
      currentHref="/add-listing-1"
      PropertyID={RouteID}
      propertyType={cachedPropertyType}
      stepCompletionStatus={stepCompletionStatus}
    >
      <div className="space-y-8">
        {/* Header Section */}
        <div className="pb-6 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Property Details
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Tell us about your property to get started. All fields marked with * are required.
          </p>
        </div>

        <form onSubmit={formik.handleSubmit} className="space-y-8">
          {/* Basic Information Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 sm:p-8">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-[#F56948] text-white flex items-center justify-center text-sm font-bold">
                1
              </span>
              Basic Information
            </h3>
            
            <div className="space-y-6">
              <FormItem
                label="Property Title *"
                desc="Create a catchy title that highlights your property's best features."
              >
                <Input
                  placeholder="e.g., Cozy Downtown Apartment with City Views"
                  name="title"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="text-base"
                />
                {formik.touched.title && formik.errors.title && (
                  <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {formik.errors.title}
                  </div>
                )}
              </FormItem>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormItem desc="Select the category that best describes your property.">
                  <FormField
                    label="Property Category *"
                    name="property_category"
                    type="select"
                    value={formik.values.property_category}
                    onChange={(e) => {
                      formik.handleChange(e);
                      formik.setFieldValue("property_type_id", "");
                    }}
                    onBlur={formik.handleBlur}
                    options={
                      propertiesCategories?.map((item) => ({
                        value: String(item.id),
                        label: item.name,
                      })) || []
                    }
                    error={
                      formik.touched.property_category &&
                      formik.errors.property_category
                        ? formik.errors.property_category
                        : undefined
                    }
                  />
                </FormItem>

                <FormItem>
                  <FormField
                    label="Property Type *"
                    name="property_type_id"
                    type="select"
                    value={formik.values.property_type_id}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    options={filteredPropertyTypes.map((type) => ({
                      value: String(type.id),
                      label: type.name,
                    }))}
                    error={
                      formik.touched.property_type_id &&
                      formik.errors.property_type_id
                        ? formik.errors.property_type_id
                        : undefined
                    }
                  />
                </FormItem>
              </div>
            </div>
          </div>

          {/* Location Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 sm:p-8">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-[#F56948] text-white flex items-center justify-center text-sm font-bold">
                2
              </span>
              Location
            </h3>
            
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormItem label="Country/Region *">
                  <Select
                    name="country"
                    value={formik.values.country}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="text-base"
                  >
                    <option value="">Select a country</option>
                    {__countryListing.map((country) => (
                      <option key={country.name} value={country.name}>
                        {country.name}
                      </option>
                    ))}
                  </Select>
                  {formik.touched.country && formik.errors.country && (
                    <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {formik.errors.country}
                    </div>
                  )}
                </FormItem>

                {formik.values.country === "United States" && (
                  <FormItem label="City">
                    <Input
                      name="city"
                      placeholder="e.g., San Francisco"
                      value={formik.values.city}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="text-base"
                    />
                    {formik.touched.city && formik.errors.city && (
                      <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {formik.errors.city}
                      </div>
                    )}
                  </FormItem>
                )}
              </div>
            </div>
          </div>

          {/* Property Details Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 sm:p-8">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-[#F56948] text-white flex items-center justify-center text-sm font-bold">
                3
              </span>
              Property Details
            </h3>
            
            <div className="space-y-6">
              <FormItem label="Maximum Number of Guests *">
                <Select
                  {...formik.getFieldProps("guests")}
                  className="text-base"
                >
                  <option value="">Select number of guests</option>
                  {Array.from({ length: 18 }, (_, i) => i + 1).map((guest) => (
                    <option key={guest} value={guest}>
                      {guest} {guest === 1 ? "Guest" : "Guests"}
                    </option>
                  ))}
                </Select>
                {formik.touched.guests && formik.errors.guests && (
                  <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {formik.errors.guests}
                  </div>
                )}
              </FormItem>

              <FormItem label="Property Description *" desc="Describe what makes your property special. Include amenities, nearby attractions, and any unique features.">
                <Textarea
                  placeholder="Share what makes your property unique and why guests will love staying here..."
                  name="description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  rows={6}
                  className="text-base"
                />
                {formik.touched.description && formik.errors.description && (
                  <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {formik.errors.description}
                  </div>
                )}
              </FormItem>

              <FormItem label="Cancellation Policy *" desc="Select a policy and view its details below.">
                <Select
                  name="cancellation_policy_id"
                  value={formik.values.cancellation_policy_id}
                  onChange={handleCancellationPolicyChange}
                  onBlur={formik.handleBlur}
                  className={`text-base ${
                    formik.touched.cancellation_policy_id &&
                    formik.errors.cancellation_policy_id
                      ? "error-border"
                      : ""
                  }`}
                >
                  <option value="">Select a cancellation policy</option>
                  {Array.isArray(filteredCancellationTypes) &&
                    filteredCancellationTypes.map((type: any) => (
                      <option key={type.id} value={String(type.id)}>
                        {type.name}
                      </option>
                    ))}
                </Select>

                {/* Expandable Policy Description */}
                {showPolicyDescription && 
                 formik.values.cancellation_policy_id && 
                 filteredCancellationTypes.length > 0 && 
                 getSelectedPolicy() && (
                  <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 dark:border-blue-400 rounded-lg animate-slideDown">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <svg 
                          className="w-5 h-5 text-blue-600 dark:text-blue-400" 
                          fill="currentColor" 
                          viewBox="0 0 20 20"
                        >
                          <path 
                            fillRule="evenodd" 
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" 
                            clipRule="evenodd" 
                          />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-1.5">
                          {getSelectedPolicy()?.name}
                        </h4>
                       <div
  className="
    ck-content prose prose-sm max-w-none
    text-blue-800 dark:text-blue-200
    [&_p]:mb-3 [&_p:last-child]:mb-0 [&_p]:leading-relaxed [&_p]:text-sm
    [&_strong]:font-bold [&_strong]:text-blue-900 dark:[&_strong]:text-blue-100
    [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_ul]:my-2
    [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1.5 [&_ol]:my-2
    [&_li]:text-sm [&_li]:leading-relaxed
  "
  dangerouslySetInnerHTML={{ __html: getSelectedPolicy()?.description || "" }}
/>
                      </div>
                    </div>
                  </div>
                )}

                {formik.touched.cancellation_policy_id &&
                  formik.errors.cancellation_policy_id && (
                    <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {typeof formik.errors.cancellation_policy_id === "string" &&
                        formik.errors.cancellation_policy_id}
                    </div>
                  )}
              </FormItem>
            </div>
          </div>

          {/* Additional Information Card (Optional) */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 sm:p-8">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 flex items-center justify-center text-sm font-bold">
                4
              </span>
              Additional Information
              <span className="text-sm font-normal text-gray-500 dark:text-gray-400">(Optional)</span>
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 ml-10">
              Provide extra details to help guests better understand your property and neighborhood.
            </p>
            
            <div className="space-y-6">
              <FormItem label="Universities Nearby" desc="List nearby universities or colleges that might be relevant for students.">
                <Textarea
                  placeholder="e.g., Stanford University (2 miles), UC Berkeley (15 miles)"
                  name="university_nearby"
                  value={formik.values.university_nearby || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  rows={3}
                  className="text-base"
                />
                {formik.touched.university_nearby &&
                  formik.errors.university_nearby && (
                    <div className="text-red-500 text-sm mt-2">
                      {formik.errors.university_nearby}
                    </div>
                  )}
              </FormItem>

              <FormItem label="About the Neighborhood" desc="Describe the neighborhood, local attractions, restaurants, and transportation options.">
                <Textarea
                  placeholder="Tell guests about the local area, things to do, safety, and what makes this neighborhood special..."
                  name="about_neighbourhoods"
                  value={formik.values.about_neighbourhoods || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  rows={4}
                  className="text-base"
                />
                {formik.touched.about_neighbourhoods &&
                  formik.errors.about_neighbourhoods && (
                    <div className="text-red-500 text-sm mt-2">
                      {formik.errors.about_neighbourhoods}
                    </div>
                  )}
              </FormItem>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Step 1 of 9 • Save and continue to the next step
            </div>
            <div className="flex gap-3">
              <ButtonPrimary 
                type="submit" 
                disabled={formik.isSubmitting}
                className="min-w-[140px]"
              >
                {formik.isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Continue
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                )}
              </ButtonPrimary>
            </div>
          </div>
        </form>
      </div>
    </CommonLayoutV2>
  );
};

export default Description;